import moment from "moment";
import { roleIds, routePrefix } from "./constant";

export const formatDate = (date, options = {}) => {
  const { format = "DD/MM/YY", ...opts } = options;
  return moment(date).format(format, opts);
};

export async function downloadFile(url, filename) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error(`HTTP ${res?.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || url.split("/").pop() || "file.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.log("Error downloading file", err);
    // Fallback: open in new tab if CORS blocks the fetch
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export const formatAddress = (addr) => {
  if (!addr) return "";
  return [
    addr.address_line1 || addr.address_line_1,
    addr.address_line2 || addr.address_line_2,
    addr.city,
    addr.state,
    addr.post_code || addr.postal_code,
    addr.country,
  ]
    .filter(Boolean)
    .join(", ");
};

export const calcInStock = (bsr_stock_cases, dcl_stockmtrs) => {
  return (
    (bsr_stock_cases ?? []).reduce(
      (sum, stockItem) => sum + Number(stockItem?.stockqty ?? "0"),
      0,
    ) > Number(dcl_stockmtrs ?? "0")
  );
};

export const roundDecimal = (num, dec = 1) => {
  return Number(num) % 1 === 0 ? Number(num) : Number(num)?.toFixed(dec);
};

/**
 * Converts inches to centimeters.
 * Accepts both numbers and numeric strings.
 * @param {number|string} inches - The length in inches.
 * @returns {number} The equivalent length in centimeters.
 */
export const inchesToCm = (inches) => {
  const formattedInches = Number(inches);
  const defaultValues = {
    36: "90",
    54: "137",
    58: "147",
  };

  if (defaultValues[formattedInches]) {
    return defaultValues[formattedInches];
  }

  const value = parseFloat(inches);

  if (isNaN(value)) {
    return 0;
  }

  return Math.round(value * 2.54);
};

export const handleRouteNav = (route, role_id) => {
  let prefix = "";
  if (role_id === roleIds.Customer) prefix = routePrefix.customer;
  if (role_id === roleIds.Admin) prefix = routePrefix.admin;
  else if (role_id === roleIds.SalesManager || role_id === roleIds.SalesPerson)
    prefix = routePrefix.sales;
  return `${prefix}${route}`;
};

export const numberFormatter = (raw, { locale = "en-IN" } = {}) => {
  if (raw === null || raw === undefined || raw === "") return 0;

  const n = typeof raw === "number" ? raw : parseFloat(String(raw).replace(/,/g, ""));

  if (!isFinite(n)) return raw;

  const opts = {
    style: "decimal",
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  };

  return new Intl.NumberFormat(locale, opts).format(n);
};

export const getSummaryDescription = (currentVariant, allVariants) => {
  if (!currentVariant || !allVariants?.length) return "";

  const isFullyCompleted = allVariants.every((item) => item?.status === "completed");

  if (isFullyCompleted) {
    return "🏆 Congratulations! You’ve achieved your target. If eligible, you may Claim Your Benefits!";
  }

  const percent = currentVariant.progress_percent || 0;
  const isTierOne = currentVariant.index === 1;

  if (isTierOne) {
    if (percent < 25) return "🛒 You haven’t tapped into this scheme yet. Place Orders today!";
    if (percent < 50) return "📈 You’re off to a good start. Keep going!";
    if (percent < 75) return "📊 You’re halfway there! Keep moving, towards your target.";
    return "🚀 You’re almost there! Keep placing orders, and you’ll achieve your target.";
  } else {
    if (percent < 25) return "🔓 You’ve unlocked a new target. Time to start placing orders again!";
    if (percent < 50)
      return "🌟 You’re off to a great start. Keep progressing, and Claim Higher Benefits!";
    if (percent < 75) return "🧭 You’re halfway there! Keep moving, towards your target.";
    return "🎯 You’re almost there! Keep placing orders, and you’ll achieve your target.";
  }
};
